/**
 * Runtime validators for Gateway JSON.
 * Interfaces alone only check at compile time — these protect against
 * malformed / partial responses when the BE contract drifts.
 */

import type {
  SessionResponse,
  WidgetAllowlist,
  WidgetBranding,
  WidgetConfig,
  WidgetFeatures,
  WidgetPopularQuestion,
  WidgetTheme,
} from '../types/api';
import { WidgetApiError } from './errors';
import { isRecord } from './typeGuards';

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

function requireString(
  record: Record<string, unknown>,
  key: string,
  context: string,
): string {
  const value = record[key];
  if (typeof value !== 'string' || !value) {
    throw new WidgetApiError(
      'invalid_response',
      `Missing or invalid "${key}" in ${context}`,
      200,
    );
  }
  return value;
}

function requireNumber(
  record: Record<string, unknown>,
  key: string,
  context: string,
): number {
  const value = record[key];
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new WidgetApiError(
      'invalid_response',
      `Missing or invalid "${key}" in ${context}`,
      200,
    );
  }
  return value;
}

function parseTheme(raw: unknown): WidgetTheme {
  const theme = isRecord(raw) ? raw : {};
  return {
    accent_color: asString(theme.accent_color),
    launcher_icon: asString(theme.launcher_icon),
    position: asString(theme.position, 'bottom-right'),
  };
}

function parseAllowlist(raw: unknown): WidgetAllowlist {
  const allow = isRecord(raw) ? raw : {};
  return {
    origins: asStringArray(allow.origins),
    mobile_bundles: asStringArray(allow.mobile_bundles),
  };
}

function parseFeatures(raw: unknown): WidgetFeatures {
  const features = isRecord(raw) ? raw : {};
  return {
    file_upload: asBoolean(features.file_upload, false),
    voice: asBoolean(features.voice, false),
    pre_chat_form: asBoolean(features.pre_chat_form, false),
  };
}

function parseBranding(raw: unknown): WidgetBranding {
  const branding = isRecord(raw) ? raw : {};
  return {
    show_powered_by: asBoolean(branding.show_powered_by, true),
  };
}

/** `false` / `"false"` hide the launcher; missing or unknown → show. */
function parseWidgetStatus(value: unknown): boolean {
  if (value === false || value === 0 || value === 'false' || value === '0') {
    return false;
  }
  return true;
}

function parsePopularQuestions(raw: unknown): WidgetPopularQuestion[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const questions = raw
    .map((item) => {
      if (!isRecord(item)) return null;
      const question = asString(item.question).trim();
      return question ? { question } : null;
    })
    .filter((item): item is WidgetPopularQuestion => item !== null);
  return questions.length > 0 ? questions : undefined;
}

/**
 * Validates GET /v1/config/{pk} body into a complete WidgetConfig.
 * Accepts both Gateway envelope `{ publishable_key, config }` and a flat config.
 */
export function parseWidgetConfig(body: unknown): WidgetConfig {
  if (!isRecord(body)) {
    throw new WidgetApiError(
      'invalid_response',
      'Config response must be a JSON object',
      200,
    );
  }

  const raw = isRecord(body.config) ? body.config : body;

  return {
    avatar_id: requireString(raw, 'avatar_id', 'config'),
    name: requireString(raw, 'name', 'config'),
    welcome_message: asString(raw.welcome_message),
    theme: parseTheme(raw.theme),
    locale_default: asString(raw.locale_default, 'en'),
    locales_supported: asStringArray(raw.locales_supported),
    allowlist: parseAllowlist(raw.allowlist),
    identity_verification: asString(raw.identity_verification, 'disabled'),
    features: parseFeatures(raw.features),
    branding: parseBranding(raw.branding),
    widget_status: parseWidgetStatus(raw.widget_status),
    popular_questions: parsePopularQuestions(raw.popular_questions),
  };
}

/** Validates POST /v1/sessions body into SessionResponse. */
export function parseSessionResponse(body: unknown): SessionResponse {
  if (!isRecord(body)) {
    throw new WidgetApiError(
      'invalid_response',
      'Session response must be a JSON object',
      200,
    );
  }

  return {
    access_token: requireString(body, 'access_token', 'session'),
    expires_in: requireNumber(body, 'expires_in', 'session'),
    session_id: requireString(body, 'session_id', 'session'),
    visitor_id: requireString(body, 'visitor_id', 'session'),
    avatar_id: requireString(body, 'avatar_id', 'session'),
  };
}

/** Narrow unknown postMessage data to a host → panel bridge envelope. */
export function isHostBridgeMessage(
  data: unknown,
): data is { source: 'avatar_host_script'; event: string; payload?: unknown } {
  if (!isRecord(data)) return false;
  return data.source === 'avatar_host_script' && typeof data.event === 'string';
}
